import dayjs from 'dayjs';
import { getUniqueId } from "../../utils";
import { dateToString } from "../utils";
import { DAYS_IN_WEEK, WEEKS_TO_SHOW } from "./constants";
export var ONE_DAY = 1000 * 60 * 60 * 24;
export var ONE_WEEK = ONE_DAY * 7;
var KNOWN_SUNDAY = new Date(Date.UTC(1970, 0, 4));
var KNOWN_SUNDAY_OFFSET = KNOWN_SUNDAY.getTimezoneOffset() * 60000; // Can be any valid number except for 1.
// 1 causes date picker to break in some time zones, because first day of the month in UTC is interpreted as last month.

var DAY_IN_MONTH = 9;
var TODAY = new Date();
export var getCalendarId = function getCalendarId() {
  return "StencilDatePicker-".concat(getUniqueId());
};
export var getCellId = function getCellId(date, calendarId) {
  return "".concat(calendarId, "-").concat(dateToString(date));
};
/**
 * Returns a 2D array of dates for the given month.
 *
 * @param year The year being displayed in the calendar
 * @param month The 0-indexed month being displayed in the calendar
 * @param weekStartDay The day of the week to start each row with (0 = Sunday)
 */

export var getDatesForMonth = function getDatesForMonth(year, month, weekStartDay) {
  var firstDateOfMonth = dayjs.utc(new Date(Date.UTC(year, month, 1)));
  var daysBeforeStart;

  if (weekStartDay < firstDateOfMonth.day()) {
    // If the desired week start day is less than firstDateOfMonth.day(), all we need to do is find the difference to know how much to pad the start of the first week by.
    // E.g. if the first day of the month is a Thursday (4) and the desired start of week to show is Tuesday (2), then we need to pad the start of the month with 2 days to get the correct week start day.
    // [ 0, 1, 2, 3, 4, 5, 6]
    //         ^-----^       abs(4 - 2) = 2
    //         2  1          (2 days to pad by)
    //      ^-----------^    abs(5 - 1) = 4
    //      4  3  2  1       (4 days to pad by)
    daysBeforeStart = Math.abs(firstDateOfMonth.day() - weekStartDay);
  } else if (firstDateOfMonth.day() < weekStartDay) {
    // If the desired start day is larger than firstDateOfMonth.day(), we need to wrap around the day indexes.
    // E.g. if the first day of the month is a Tuesday (2) and the desired start of the week to show is Thursday (4), we need to pad the start of the month with 5 days to get the correct week start day.
    // [ 0, 1, 2, 3, 4, 5, 6]
    //  -------^     ^------ 7 - abs(2 - 4) = 5
    //   2  1        5  4  3 (5 days to pad by)
    //  ----^           ^--- 7 - abs(1 - 5) = 3
    //   1              3  2 (3 days to pad by)
    daysBeforeStart = 7 - Math.abs(weekStartDay - firstDateOfMonth.day());
  } else {
    daysBeforeStart = 0;
  }

  var firstDateToShow = dayjs.utc(firstDateOfMonth).subtract(daysBeforeStart, 'day');
  var result = [];

  for (var week = 0; week < WEEKS_TO_SHOW; week++) {
    var weekArray = [];

    for (var day = 0; day < 7; day++) {
      var date = firstDateToShow.add(day, 'day').add(week, 'w').toDate();
      weekArray.push(date);
    }

    result.push(weekArray);
  }

  return result;
};
/**
 * Returns the month and year as a string for the given locale.
 *
 * @param year The year being displayed in the calendar
 * @param month The 0-indexed month being displayed in the calendar
 * @param locale The Unicode locale code
 */

export var getMonthAndYearString = function getMonthAndYearString(year, month, locale) {
  return new Date(Date.UTC(year, month, DAY_IN_MONTH)).toLocaleString(locale, {
    year: 'numeric',
    month: 'long'
  });
};
export var addMonth = function addMonth(month, year) {
  if (month === 11) {
    return [0, year + 1];
  } else {
    return [month + 1, year];
  }
};
export var subtractMonth = function subtractMonth(month, year) {
  if (month === 0) {
    return [11, year - 1];
  } else {
    return [month - 1, year];
  }
};

var getDay = function getDay(dayOfWeek) {
  return new Date(KNOWN_SUNDAY.getTime() + dayOfWeek * ONE_DAY + KNOWN_SUNDAY_OFFSET);
};

export var getDayString = function getDayString(dayOfWeek, locale) {
  return getDay(dayOfWeek).toLocaleString(locale, {
    weekday: 'short'
  });
};
export var getFullDayString = function getFullDayString(dayOfWeek, locale) {
  return getDay(dayOfWeek).toLocaleString(locale, {
    weekday: 'long'
  });
};
export var dateAppearsInMonth = function dateAppearsInMonth(date, month, year) {
  return date.getUTCMonth() === month && date.getUTCFullYear() === year;
};
export var areDatesSameDay = function areDatesSameDay(a, b) {
  return dayjs.utc(a).isSame(dayjs.utc(b), 'day');
};
export var isToday = function isToday(date) {
  return areDatesSameDay(date, TODAY);
};
export var isDateVisible = function isDateVisible(date, firstVisibleDate, lastVisibleDate) {
  return date >= firstVisibleDate && date <= lastVisibleDate;
};
export var isDateFocusable = function isDateFocusable(_ref) {
  var date = _ref.date,
      dates = _ref.dates,
      selectionStart = _ref.selectionStart,
      selectionEnd = _ref.selectionEnd,
      month = _ref.month,
      isDateDisabled = _ref.isDateDisabled;
  var firstVisibleDate = dates[0][0];
  var lastVisibleDate = dates[WEEKS_TO_SHOW - 1][DAYS_IN_WEEK - 1]; // If the date is the first selected date, make the date focusable.

  if (selectionStart && areDatesSameDay(selectionStart, date)) {
    return true;
  } // If the date is the last selected date, make the date focusable.


  if (selectionEnd && areDatesSameDay(selectionEnd, date)) {
    return true;
  }

  if (!selectionStart || selectionStart && !isDateVisible(selectionStart, firstVisibleDate, lastVisibleDate)) {
    if (isDateVisible(TODAY, firstVisibleDate, lastVisibleDate)) {
      // If today is disabled, make the first enabled date focusable.
      if (!!isDateDisabled && isDateDisabled(dateToString(TODAY))) {
        var prevDayDate = new Date(date);
        prevDayDate.setDate(date.getDate() - 1);
        return isDateDisabled(dateToString(prevDayDate));
      }

      if (isToday(date)) {
        return true;
      }
    } // If today is not visible, make the first date of the current month focusable if it is not disabled.
    else if (date.getUTCDate() === 1 && date.getUTCMonth() === month && (!isDateDisabled || !isDateDisabled(dateToString(date)))) {
      return true;
    }
  }

  return false;
};